import type { ApiResponse, ListMentionsResponse, Mention } from '@fluenceur/types';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from '@/libs/axiosInstance';

interface UseMentionsQueryOptions {
  type?: 'story' | 'post' | 'reel_caption';
  platform?: string;
  creatorIds?: string[];
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  per_page?: number;
  initialData?: ListMentionsResponse;
}

interface FetchMentionsParams {
  page?: number;
  per_page?: number;
  type?: 'story' | 'post' | 'reel_caption';
  platform?: string;
  social_profile_ids?: string;
  start_date?: string;
  end_date?: string;
}

const fetchMentions = async (params: FetchMentionsParams = {}): Promise<ListMentionsResponse> => {
  try {
    const response = await api.get<ApiResponse<ListMentionsResponse>>('/dashboard/mentions', {
      params,
    });
    return response.data.data as ListMentionsResponse;
  } catch (error) {
    console.error('Error fetching mentions:', error);
    throw error;
  }
};

export const useMentionsQuery = (options: UseMentionsQueryOptions = {}) => {
  const {
    type,
    platform,
    creatorIds,
    startDate,
    endDate,
    enabled = true,
    per_page = 15,
    initialData,
  } = options;

  const joinedCreatorIds = creatorIds && creatorIds.length > 0 ? creatorIds.join(',') : undefined;
  const normalizedStartDate = startDate || undefined;
  const normalizedEndDate = endDate || undefined;

  return useInfiniteQuery({
    queryKey: [
      'mentions',
      {
        type,
        platform,
        creatorIds: joinedCreatorIds,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        perPage: per_page,
      },
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const params: FetchMentionsParams = {
        page: pageParam,
        per_page,
        ...(type && { type }),
        ...(platform && { platform }),
        // The creator filter lists social profiles; the backend reads them under
        // this name and silently drops any other key.
        ...(joinedCreatorIds && { social_profile_ids: joinedCreatorIds }),
        ...(normalizedStartDate && { start_date: normalizedStartDate }),
        ...(normalizedEndDate && { end_date: normalizedEndDate }),
      };

      return await fetchMentions(params);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        console.warn('lastPage is undefined');
        return undefined;
      }

      if (!lastPage.meta) {
        console.warn('lastPage.meta is undefined, lastPage:', lastPage);
        return undefined;
      }

      const { currentPage, lastPage: totalPages } = lastPage.meta;

      if (typeof currentPage !== 'number' || typeof totalPages !== 'number') {
        console.warn('Invalid pagination data:', { currentPage, totalPages });
        return undefined;
      }

      if (currentPage < totalPages) {
        return currentPage + 1;
      }

      return undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...(initialData && {
      initialData: {
        pages: [initialData],
        pageParams: [1],
      },
    }),
  });
};

export const useFlattenedMentions = (options: UseMentionsQueryOptions = {}) => {
  const query = useMentionsQuery(options);

  // Same array until a page arrives: the grid and its stats batches key on it.
  const pages = query.data?.pages;
  const mentions: Mention[] = useMemo(
    () => pages?.flatMap((page) => page?.data || []) ?? [],
    [pages]
  );
  const total = query.data?.pages[0]?.meta?.total ?? 0;

  return {
    ...query,
    mentions,
    total,
  };
};

export { fetchMentions };
